import React, { useCallback, useMemo, useRef, useState } from "react";
import Page from "components/Page";
import { Card, Input, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Loader from "components/Loader";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import OfficePolicyStyle from "../../OfficePolicyManagement.module.scss";
import { useViewPolicy } from "./useViewPolicy";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { useStaffDesignation } from "repositories/scheduler-repository";
import { formatCalendarDate, formatLocalDateTime } from "utils";
import AssigneeCard from "./AssigneeCard";
import CancelAssignmentModal from "../../Modals/CancelAssignmentModal";
import Empty from "components/Empty";
import { prioritiesOptions } from "constants";

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    height: "50px",
    borderRadius: "4px",
    borderColor: "#E0E0E0",
    boxShadow: "none",
    fontSize: "13px",
    fontWeight: "500",
    color: "#587E85",
    "&:hover": { borderColor: "#E0E0E0" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#587E85" }),
  singleValue: (provided) => ({ ...provided, color: "#587E85" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({ ...provided, color: "#587E85" }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

function getPriorityBadgeClass(priorityId) {
  const option = (prioritiesOptions || []).find(
    (o) => Number(o.id) === Number(priorityId),
  );
  const key = (option?.name ?? "Low").toLowerCase();
  return (
    OfficePolicyStyle[`badge-priority-${key}`] ??
    OfficePolicyStyle["badge-priority-low"]
  );
}

function ViewPolicy() {
  const { t } = useTranslation();
  const { effectiveOwnerId } = useAccountOwnerCommon();
  const {
    policy,
    policyId,
    policyError,
    isLoading,
    assignmentsLoading,
    refetch,
    refetchAssignments,
    officesLabel,
    totalAssignees,
    oneAssigneeDisplayName,
    filteredAssignees,
    officeOptions,
    statusOptions,
    searchName,
    setSearchName,
    selectedOffice,
    setSelectedOffice,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    deleteModalOpen,
    setDeleteModalOpen,
    onBack,
    handleEdit,
    handleViewLogs,
    handleDeleteConfirm,
    handleReNotify,
    handleAssignmentAction,
    handleReassign,
    goToComments,
    handleToggleChange,
    getStatusLabel,
    getPriorityLabel,
    getPolicyStatusLabel,
    getFilenameFromPath,
    hasNextAssigneesPage,
    loadMoreAssignees,
    isFetchingNextAssigneesPage,
  } = useViewPolicy();

  const [showLoader, setShowLoader] = useState(false);
  const assigneeListRef = useRef(null);

  const handleAssigneesScroll = useCallback(
    (e) => {
      if (!hasNextAssigneesPage || isFetchingNextAssigneesPage) return;
      const el = e?.currentTarget;
      if (!el) return;
      const threshold = 80;
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreAssignees();
      }
    },
    [hasNextAssigneesPage, isFetchingNextAssigneesPage, loadMoreAssignees],
  );
  const rolesOwnerId = policy?.policyFolder?.ownerId ?? effectiveOwnerId;
  const { data: rolesData } = useStaffDesignation(
    { selectedId: rolesOwnerId },
    { enabled: !!rolesOwnerId },
  );
  const roleOptions = useMemo(() => {
    const raw = rolesData ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const flat =
      list.length > 0 && list.some((r) => Array.isArray(r?.designations))
        ? list.flatMap((r) => r.designations ?? [])
        : list;
    return [
      { value: "", label: t("officePolicyManagement.allRoles") || "All Roles" },
      ...flat
        .filter((r) => (r?.id ?? r?.designationId) != null)
        .map((r) => ({
          value: String(r.id ?? r.designationId ?? ""),
          label: r.name ?? r.designation ?? "",
        })),
    ];
  }, [rolesData, t]);

  const assignedToText =
    totalAssignees === 1
      ? oneAssigneeDisplayName
      : `${totalAssignees} ${t("officePolicyManagement.assignees")}`;

  const handleDownloadAttachment = useCallback(
    async (filePath, fileName) => {
      try {
        setShowLoader(true);
        const isAbsolute =
          filePath.startsWith("http://") || filePath.startsWith("https://");
        const url = isAbsolute
          ? filePath
          : `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT || ""}.blob.core.windows.net/${filePath}`;
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) throw new Error("Download failed");
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || getFilenameFromPath(filePath);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
      } catch (err) {
        console.error("Failed to download file.", err);
      } finally {
        setShowLoader(false);
      }
    },
    [getFilenameFromPath],
  );

  const policyDocUrls =
    policy?.policyDocumentUrls ?? policy?.PolicyDocumentUrls ?? [];
  const attachments = useMemo(
    () =>
      policyDocUrls
        .filter((d) => !d.isDeleted && !d.IsDeleted)
        .map((d) => ({
          name: getFilenameFromPath(d.filePath ?? d.FilePath ?? ""),
          filePath: d.filePath ?? d.FilePath ?? "",
        })),
    [policyDocUrls, getFilenameFromPath],
  );

  const urlsList = useMemo(() => {
    const u = policy?.urls ?? policy?.Urls ?? [];
    if (!Array.isArray(u)) return [];
    return u
      .filter((x) => !x.isDeleted && !x.IsDeleted)
      .map((x) => x.filePath ?? x.FilePath ?? x.path ?? "");
  }, [policy]);

  const statusLabel = getPolicyStatusLabel(policy);
  const priorityLabel = getPriorityLabel(policy?.priority);
  const creationDate = policy?.createdAt
    ? formatLocalDateTime(policy.createdAt, "MMM DD, YYYY")
    : "";
  const docName = policy?.title ?? "";
  const categoryName = policy?.policyFolder?.policyCategoryTitle ?? "";
  const notes = policy?.notes ?? "";
  const version = policy?.versionNumber ?? "";
  const startDate = policy?.startDate
    ? formatCalendarDate(policy.startDate)
    : "";
  const dueDate = policy?.dueDate ? formatCalendarDate(policy.dueDate) : "";
  const createdBy =
    policy?.createdByUserName ??
    policy?.createdBy ??
    (policy?.policyFolder?.policyCategory?.owner
      ? `${policy.policyFolder.policyCategory.owner.firstName ?? ""} ${policy.policyFolder.policyCategory.owner.lastName ?? ""}`.trim()
      : "") ??
    "";

  const toggles = useMemo(
    () => [
      {
        field: "notifyAssignees",
        label: t("officePolicyManagement.notifyAssigneesaboutthePolicy"),
        checked: !!(
          policy?.notifyAssignees ??
          policy?.NotifyAssignees ??
          policy?.isNotifyAssignees
        ),
        valueWhenOn: true,
        valueWhenOff: false,
      },
      {
        field: "isNotifyManagement",
        label: t("officePolicyManagement.notifyAssigneesabouttheUpdates"),
        checked: !!(
          policy?.isNotifyManagement ??
          policy?.notifyAssignor ??
          policy?.NotifyAssignor
        ),
        valueWhenOn: true,
        valueWhenOff: false,
      },
      {
        field: "remindAfterPublish",
        label: t("officePolicyManagement.remindAssigneesAfterPublish", {
          count: policy?.remindAfterPublish ?? policy?.RemindAfterPublish ?? 0,
        }),
        checked: !!(
          (policy?.remindAfterPublish ?? policy?.RemindAfterPublish ?? 0) > 0
        ),
        valueWhenOn:
          Math.max(
            1,
            policy?.remindAfterPublish ?? policy?.RemindAfterPublish ?? 0,
          ) || 1,
        valueWhenOff: 0,
      },
      {
        field: "remindBeforeDueDate",
        label: t("officePolicyManagement.remindAssigneesBeforeDue", {
          count:
            policy?.remindBeforeDueDate ?? policy?.RemindBeforeDueDate ?? 0,
        }),
        checked: !!(
          (policy?.remindBeforeDueDate ?? policy?.RemindBeforeDueDate ?? 0) > 0
        ),
        valueWhenOn:
          Math.max(
            1,
            policy?.remindBeforeDueDate ?? policy?.RemindBeforeDueDate ?? 0,
          ) || 1,
        valueWhenOff: 0,
      },
    ],
    [policy, t],
  );

  const onToggleChange = useCallback(
    (toggle) => {
      if (!handleToggleChange) return;
      const newValue = toggle.checked
        ? toggle.valueWhenOff
        : toggle.valueWhenOn;
      handleToggleChange(toggle.field, newValue);
    },
    [handleToggleChange],
  );

  const handleCardClick = useCallback(
    (e, assignee) => {
      if (
        e.target.closest(".interactive-element") ||
        e.target.closest(`.${OfficePolicyStyle["kebab-btn"]}`)
      )
        return;
      goToComments(assignee?.assignmentId);
    },
    [goToComments],
  );

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelAssignmentId, setCancelAssignmentId] = useState(null);

  const handleCancelClick = useCallback((assignmentId) => {
    setCancelAssignmentId(assignmentId);
    setCancelModalOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(
    (assignmentId) => {
      if (assignmentId) handleAssignmentAction(assignmentId, "Cancel");
      setCancelAssignmentId(null);
      setCancelModalOpen(false);
    },
    [handleAssignmentAction],
  );

  const getButtonClassForCard = useCallback((statusLabel) => {
    const statusClasses = {
      Pending: OfficePolicyStyle.Pending,
      Cancelled: OfficePolicyStyle.Cancelled,
      Started: OfficePolicyStyle.Started,
      Accepted: OfficePolicyStyle.Accepted,
      Rejected: OfficePolicyStyle.Rejected,
      InProgress: OfficePolicyStyle.InProgress,
      Completed: OfficePolicyStyle.Completed,
    };
    const key = (statusLabel || "").replace(/\s+/g, "");
    return `${OfficePolicyStyle.task_management_card_btn} ${statusClasses[statusLabel] || statusClasses[key] || ""}`.trim();
  }, []);

  const cx = (...classes) => classes.filter(Boolean).join(" ");

  if (!policyId || policyError) {
    return (
      <Page onBack={onBack} title={t("officePolicyManagement.policyDetails")}>
        <Card
          className={cx(
            "form-wrapper margin30 border-0",
            OfficePolicyStyle["view-policy-container"],
          )}
        >
          <p className="mb-0">{t("officePolicyManagement.policyNotFound")}</p>
        </Card>
      </Page>
    );
  }

  if (isLoading || !policy) {
    return (
      <Page onBack={onBack} title={t("officePolicyManagement.policyDetails")}>
        <Loader />
      </Page>
    );
  }

  return (
    <Page onBack={onBack} title={t("officePolicyManagement.policyDetails")}>
      {showLoader && <Loader />}
      <Card
        className={cx(
          "form-wrapper margin30 border-0",
          OfficePolicyStyle["view-policy-container"],
        )}
      >
        {officesLabel && (
          <div className={OfficePolicyStyle["policy-banner"]}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 1.99609C17.5228 1.99609 22 6.47325 22 11.9961C22 17.5189 17.5228 21.9961 12 21.9961C6.47715 21.9961 2 17.5189 2 11.9961C2 6.47325 6.47715 1.99609 12 1.99609Z"
                stroke="black"
                strokeWidth="1.96364"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 15.9961V11.9961"
                stroke="black"
                strokeWidth="1.96364"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8.00391H12.0088"
                stroke="black"
                strokeWidth="1.96364"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              <strong>
                {t("officePolicyManagement.thisPolicyAppliesTo") ||
                  "This policy applies to:"}
              </strong>{" "}
              {policy?.policyFolder?.isAllOffice
                ? t("accountOwner.allOffices")
                : officesLabel}
            </span>
          </div>
        )}

        <div className={OfficePolicyStyle["policy-header-row"]}>
          <h2 className={OfficePolicyStyle["policy-title"]}>
            {policy?.policyFolder?.title ?? ""}
          </h2>
          <div className={OfficePolicyStyle["header-actions"]}>
            <button
              type="button"
              className={OfficePolicyStyle["icon-btn"]}
              onClick={handleViewLogs}
              title={t("officePolicyManagement.viewLogs")}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 4V10.025V10V20V4ZM5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V4C3 3.45 3.19583 2.97917 3.5875 2.5875C3.97917 2.19583 4.45 2 5 2H13L19 8V10.5C18.6833 10.3667 18.3583 10.2625 18.025 10.1875C17.6917 10.1125 17.35 10.0583 17 10.025V9H12V4H5V20H11.025C11.2917 20.4 11.5917 20.7708 11.925 21.1125C12.2583 21.4542 12.625 21.75 13.025 22H5Z"
                  fill="#587E85"
                />
                <path
                  d="M16.5 21.5C15.1167 21.5 13.9375 21.0125 12.9625 20.0375C11.9875 19.0625 11.5 17.8833 11.5 16.5C11.5 15.1167 11.9875 13.9375 12.9625 12.9625C13.9375 11.9875 15.1167 11.5 16.5 11.5C17.8833 11.5 19.0625 11.9875 20.0375 12.9625C21.0125 13.9375 21.5 15.1167 21.5 16.5C21.5 17.8833 21.0125 19.0625 20.0375 20.0375C19.0625 21.0125 17.8833 21.5 16.5 21.5ZM17 16.3V13.5H16V16.7L18.15 18.85L18.85 18.15L17 16.3Z"
                  fill="#587E85"
                />
              </svg>
            </button>
            <button
              type="button"
              className={OfficePolicyStyle["icon-btn"]}
              onClick={() => setDeleteModalOpen(true)}
              title={t("officePolicyManagement.delete")}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M13.666 2C15.0821 2 16.2411 3.10387 16.3281 4.49805L16.333 4.66699V5.55566H19.8887C20.3796 5.55566 20.7773 5.95347 20.7773 6.44434C20.7773 6.89749 20.4383 7.27132 20 7.32617L19.8887 7.33301H19V18.8887C19 20.3047 17.896 21.4636 16.502 21.5508L16.333 21.5557H7.44434C6.02844 21.5554 4.86925 20.4516 4.78223 19.0576L4.77734 18.8887V7.33301H3.88867C3.39785 7.33289 3 6.93518 3 6.44434C3.00005 5.99124 3.33911 5.61737 3.77734 5.5625L3.88867 5.55566H7.44434V4.66699C7.44434 3.25102 8.5484 2.0921 9.94238 2.00488L10.1104 2H13.666ZM6.55469 7.33301V18.8887C6.55469 19.3415 6.8942 19.7152 7.33203 19.7705L7.44434 19.7773H16.333C16.7861 19.7773 17.16 19.4382 17.2148 19L17.2217 18.8887V7.33301H6.55469ZM10.1104 3.77734C9.65769 3.77772 9.28353 4.11686 9.22852 4.55469L9.22168 4.66699V5.55566H14.5547V4.66699C14.5547 4.21384 14.2156 3.84001 13.7773 3.78516L13.666 3.77734H10.1104Z"
                  fill="#587E85"
                />
              </svg>
            </button>
            <button
              type="button"
              className={OfficePolicyStyle["icon-btn"]}
              onClick={handleEdit}
              title={t("officePolicyManagement.edit")}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15.2607 4.8125C15.9617 4.11159 16.9839 3.83818 17.9414 4.09473C18.8987 4.35135 19.6468 5.09928 19.9033 6.05664C20.1598 7.01403 19.8854 8.03548 19.1846 8.73633L8.07422 19.8477C7.96038 19.9615 7.81729 20.0419 7.66113 20.0811L4.10547 20.9707C3.45455 21.1334 2.8648 20.5435 3.02734 19.8926L3.91602 16.3369C3.95509 16.1806 4.03647 16.0377 4.15039 15.9238L15.2607 4.8125ZM19.8896 19.2188C20.3804 19.2188 20.7781 19.6167 20.7783 20.1074C20.7783 20.5606 20.4393 20.9354 20.001 20.9902L19.8896 20.9971H11.8896C11.3988 20.997 11.001 20.5983 11.001 20.1074C11.0012 19.6545 11.3402 19.2804 11.7783 19.2256L11.8896 19.2188H19.8896ZM17.3516 5.78613C17.0476 5.74668 16.7389 5.84927 16.5186 6.06934L5.58203 17.0049L5.11035 18.8857L6.99121 18.415L17.9277 7.47949C18.148 7.2592 18.2513 6.95056 18.2119 6.64648L18.1855 6.5166C18.0934 6.17278 17.8252 5.90377 17.4814 5.81152L17.3516 5.78613Z"
                  fill="#587E85"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className={OfficePolicyStyle["policy-meta-row"]}>
          <span className={OfficePolicyStyle["badge-status-blue"]}>
            {statusLabel || "—"}
          </span>
          <span className={getPriorityBadgeClass(policy.priority)}>
            <span className={OfficePolicyStyle["dot"]}>●</span>{" "}
            {priorityLabel || "—"}
          </span>
          <span className={OfficePolicyStyle["meta-date"]}>{creationDate}</span>
        </div>

        <div className={OfficePolicyStyle["policy-content-grid"]}>
          <div className={OfficePolicyStyle["policy-details-panel"]}>
            <DetailItem
              label={t("officePolicyManagement.documentName")}
              value={docName || "—"}
              bold
            />
            <DetailItem
              label={t("officePolicyManagement.category")}
              value={categoryName || "—"}
              bold
            />
            <DetailItem
              label={t("officePolicyManagement.note")}
              value={notes || "—"}
              bold
            />
            <DetailItem
              label={t("officePolicyManagement.versionNumber")}
              value={version || "—"}
              bold
            />
            <div className={OfficePolicyStyle["details-grid-row"]}>
              <DetailItem
                label={t("officePolicyManagement.startDate")}
                value={startDate}
                bold
              />
              <DetailItem
                label={t("officePolicyManagement.dueDate")}
                value={dueDate}
                bold
              />
            </div>
            <div className={OfficePolicyStyle["details-grid-row"]}>
              <DetailItem
                label={t("officePolicyManagement.createdBy")}
                value={createdBy || "—"}
                bold
              />
              <DetailItem
                label={t("officePolicyManagement.assignedTo")}
                value={assignedToText || "—"}
                bold
              />
            </div>
            {urlsList.some((url) => url?.trim()) && (
              <div className={OfficePolicyStyle["detail-group"]}>
                <label>{t("officePolicyManagement.url")}</label>
                <div className={OfficePolicyStyle["url-list"]}>
                  {urlsList
                    .filter((url) => url?.trim())
                    .map((url, i) => (
                      <a
                        key={i}
                        href={url.startsWith("http") ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={OfficePolicyStyle["link-text"]}
                      >
                        {url}
                      </a>
                    ))}
                </div>
              </div>
            )}
            {attachments.length > 0 && (
              <div className={OfficePolicyStyle["detail-group"]}>
                <label>{t("officePolicyManagement.attachments")}</label>
                <div className={OfficePolicyStyle["attachment-list"]}>
                  {attachments.map((file, i) => (
                    <div
                      key={i}
                      className={OfficePolicyStyle["attachment-item"]}
                    >
                      <span className={OfficePolicyStyle["file-name"]}>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className={OfficePolicyStyle["download-link"]}
                        onClick={() =>
                          handleDownloadAttachment(file.filePath, file.name)
                        }
                      >
                        {t("officePolicyManagement.download")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={OfficePolicyStyle["toggles-section"]}>
              {toggles.map((t, i) => (
                <div key={i} className={OfficePolicyStyle["toggle-row"]}>
                  <label className={OfficePolicyStyle["switch"]}>
                    <input
                      type="checkbox"
                      checked={t.checked}
                      onChange={() => onToggleChange(t)}
                      aria-label={t.label}
                    />
                    <span
                      className={cx(
                        OfficePolicyStyle["slider"],
                        OfficePolicyStyle["round"],
                      )}
                    />
                  </label>
                  <span className={OfficePolicyStyle["toggle-label"]}>
                    {t.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={OfficePolicyStyle["policy-assignees-panel"]}>
            <h3 className={OfficePolicyStyle["panel-title"]}>
              {t("officePolicyManagement.assignees")} ({totalAssignees})
            </h3>
            <div className={OfficePolicyStyle["search-box-simple"]}>
              <svg
                className={OfficePolicyStyle["search-icon"]}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#999"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <Input
                placeholder={t("officePolicyManagement.searchByName")}
                className={OfficePolicyStyle["search-input"]}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </div>
            <div className={OfficePolicyStyle["dropdown_wrapper"]}>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={officeOptions}
                value={
                  officeOptions.find(
                    (o) => o.value === selectedOffice?.value,
                  ) ?? selectedOffice
                }
                onChange={setSelectedOffice}
                styles={customSelectStyles}
                placeholder={t("accountOwner.allOffices")}
              />
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={roleOptions}
                value={
                  roleOptions.find((o) => o.value === selectedRole?.value) ??
                  selectedRole
                }
                onChange={setSelectedRole}
                styles={customSelectStyles}
                placeholder={t("officePolicyManagement.allRoles")}
              />
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={statusOptions}
                value={
                  statusOptions.find(
                    (o) => o.value === selectedStatus?.value,
                  ) ?? selectedStatus
                }
                onChange={setSelectedStatus}
                styles={customSelectStyles}
                placeholder={t("officePolicyManagement.allStatus")}
              />
            </div>
            <div
              ref={assigneeListRef}
              className={OfficePolicyStyle["assignee-list"]}
              onScroll={handleAssigneesScroll}
            >
              {assignmentsLoading && (
                <div className={OfficePolicyStyle["assignee-list-loader"]}>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="loader-spinner"
                  />
                </div>
              )}
              {!assignmentsLoading && filteredAssignees.length === 0 && (
                <div className={OfficePolicyStyle["not_found"]}>
                  <Empty
                    Message={t("officePolicyManagement.assigneNotFound")}
                  />
                </div>
              )}
              {!assignmentsLoading &&
                filteredAssignees.length > 0 &&
                filteredAssignees.map((person, index) => (
                  <AssigneeCard
                    key={`assignee-${person.assignmentId ?? person.id ?? index}-${index}`}
                    assignee={person}
                    getButtonClass={getButtonClassForCard}
                    onCardClick={handleCardClick}
                    onCancelClick={handleCancelClick}
                    onReassign={handleReassign}
                  />
                ))}
              {isFetchingNextAssigneesPage && (
                <div
                  className={`${OfficePolicyStyle["assignee-list-loader"]} ${OfficePolicyStyle["assignee-list-loader--more"]}`}
                >
                  <Spinner
                    animation="border"
                    size="sm"
                    className="loader-spinner"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <DeleteConfirmationModal
        confirmDelete={deleteModalOpen}
        closeDeleteModal={() => setDeleteModalOpen(false)}
        confirmDeleteMessage={handleDeleteConfirm}
        title={t("officePolicyManagement.deletePolicy")}
        deleteMessage={t("officePolicyManagement.deletePolicyMessage")}
        btnText={t("officePolicyManagement.delete")}
      />
      <CancelAssignmentModal
        isOpen={cancelModalOpen}
        toggle={() => {
          setCancelModalOpen(false);
          setCancelAssignmentId(null);
        }}
        onConfirm={handleCancelConfirm}
        assignmentId={cancelAssignmentId}
      />
    </Page>
  );
}

function DetailItem({ label, value, bold }) {
  return (
    <div className={OfficePolicyStyle["detail-group"]}>
      <label>{label}</label>
      <div
        className={cx(
          OfficePolicyStyle["value"],
          bold && OfficePolicyStyle["dark-text"],
        )}
      >
        {value}
      </div>
    </div>
  );
}

export default ViewPolicy;
